/**
 * Meanbook Note - Settings & Staff Management Module
 * ควบคุมหน้าต่างการตั้งค่า (Settings Modal), การจัดการสิทธิ์พนักงาน (Staff Management),
 * การทดสอบ/อัปโหลดเสียงแจ้งเตือน (Sound Settings), และการตั้งเวลาทำงาน
 */

import { doc, setDoc, deleteDoc, updateDoc, addDoc, collection, query, getDocs, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage, auth } from "../firebase.js";
import { hasRole } from "./auth.js";
import { getCarrierSettings } from "./carrier.js";
import { showToast, showConfirm, escapeHTML } from "./uiHelpers.js";
import { triggerNotificationSound, sendLocalWebNotification, populateTimeDropdowns } from "./sound.js";

let callbacks = {
  getCurrentUserStaffData: () => null,
  getCleanupNotes: () => [],
  getActiveNoteId: () => "temp-note",
  onRenderOrderDashboard: () => {},
  onSelectNote: () => {},
  onSetupPushNotifications: () => {},
  onReturnToPackerSelect: () => {}
};

let returnToPackerSelectAfterSettings = false;

export function setReturnToPackerSelectAfterSettings(val) {
  returnToPackerSelectAfterSettings = val;
}

export function switchSettingsTab(tabName) {
  const tabCarriers = document.getElementById("settings-tab-carriers");
  const tabRoles = document.getElementById("settings-tab-roles");
  const tabCleanup = document.getElementById("settings-tab-cleanup");
  const tabPackers = document.getElementById("settings-tab-packers");
  const tabNotifications = document.getElementById("settings-tab-notifications");

  const contentCarriers = document.getElementById("settings-content-carriers");
  const contentRoles = document.getElementById("settings-content-roles");
  const contentCleanup = document.getElementById("settings-content-cleanup");
  const contentPackers = document.getElementById("settings-content-packers");
  const contentNotifications = document.getElementById("settings-content-notifications");

  if (tabCarriers) tabCarriers.classList.remove("active");
  if (tabRoles) tabRoles.classList.remove("active");
  if (tabCleanup) tabCleanup.classList.remove("active");
  if (tabPackers) tabPackers.classList.remove("active");
  if (tabNotifications) tabNotifications.classList.remove("active");

  if (contentCarriers) contentCarriers.style.display = "none";
  if (contentRoles) contentRoles.style.display = "none";
  if (contentCleanup) contentCleanup.style.display = "none";
  if (contentPackers) contentPackers.style.display = "none";
  if (contentNotifications) contentNotifications.style.display = "none";

  if (tabName === "carriers") {
    if (tabCarriers) tabCarriers.classList.add("active");
    if (contentCarriers) contentCarriers.style.display = "block";
  } else if (tabName === "cleanup") {
    if (tabCleanup) tabCleanup.classList.add("active");
    if (contentCleanup) contentCleanup.style.display = "block";
  } else if (tabName === "packers") {
    if (tabPackers) tabPackers.classList.add("active");
    if (contentPackers) contentPackers.style.display = "block";
  } else if (tabName === "notifications") {
    if (tabNotifications) tabNotifications.classList.add("active");
    if (contentNotifications) contentNotifications.style.display = "block";
  } else {
    if (tabRoles) tabRoles.classList.add("active");
    if (contentRoles) contentRoles.style.display = "block";
  }

  if (window.lucide) window.lucide.createIcons();
}

export function updateCustomSoundUI(soundType) {
  const uploadContainer = document.getElementById("custom-sound-upload-container");
  const filenameSpan = document.getElementById("custom-sound-filename");
  const btnClearCustom = document.getElementById("btn-noti-clear-custom");

  if (!uploadContainer || !filenameSpan || !btnClearCustom) return;

  if (soundType && soundType.startsWith("custom")) {
    uploadContainer.style.display = "block";
    const slotIndex = soundType.replace("custom", ""); // เช่น "1", "2", "3"
    const filename = localStorage.getItem(`globalCustomFilename_custom${slotIndex}`) || "";
    if (filename) {
      filenameSpan.textContent = `📁 ไฟล์เสียงพิเศษที่ ${slotIndex} (บนคลาวด์): ${filename}`;
      btnClearCustom.style.display = "block";
    } else {
      filenameSpan.textContent = `ยังไม่มีการอัปโหลดไฟล์เสียงพิเศษที่ ${slotIndex}`;
      btnClearCustom.style.display = "none";
    }
  } else {
    uploadContainer.style.display = "none";
  }
}

export function loadNotificationSettings() {
  const enabled = localStorage.getItem("notiEnabled") !== "false";
  const days = JSON.parse(localStorage.getItem("notiDays") || "[1,2,3,4,5,6,0]");
  const startTime = localStorage.getItem("notiStartTime") || "08:00";
  const endTime = localStorage.getItem("notiEndTime") || "18:00";
  const soundType = localStorage.getItem("notiSoundType") || "default";

  populateTimeDropdowns();

  const enabledInput = document.getElementById("setting-noti-enabled");
  if (enabledInput) enabledInput.checked = enabled;

  const [sh, sm] = startTime.split(":");
  const startHour = document.getElementById("setting-noti-start-hour");
  const startMin = document.getElementById("setting-noti-start-minute");
  if (startHour) startHour.value = sh || "08";
  if (startMin) startMin.value = sm || "00";

  const [eh, em] = endTime.split(":");
  const endHour = document.getElementById("setting-noti-end-hour");
  const endMin = document.getElementById("setting-noti-end-minute");
  if (endHour) endHour.value = eh || "18";
  if (endMin) endMin.value = em || "00";

  const soundTypeSelect = document.getElementById("setting-noti-sound-type");
  if (soundTypeSelect) soundTypeSelect.value = soundType;

  const dayButtons = document.querySelectorAll("#noti-days-container .btn-day-toggle");
  dayButtons.forEach(btn => {
    const day = parseInt(btn.getAttribute("data-day"), 10);
    if (days.includes(day)) {
      btn.classList.add("active");
    } else {
      btn.classList.remove("active");
    }
  });

  updateCustomSoundUI(soundType);

  const currentUserStaff = callbacks.getCurrentUserStaffData();
  const userEmail = currentUserStaff ? currentUserStaff.email : (auth.currentUser ? auth.currentUser.email : null);
  if (userEmail && typeof callbacks.onSetupPushNotifications === "function") {
    callbacks.onSetupPushNotifications(userEmail);
  }
}

export function showSettings() {
  const settingsPanel = document.getElementById("settings-panel");
  if (!settingsPanel) return;

  const settings = getCarrierSettings();

  const setVal = (id, arr) => {
    const el = document.getElementById(id);
    if (el) el.value = (arr || []).join(", ");
  };

  setVal("setting-prefix-ems", settings.ems);
  setVal("setting-prefix-flash", settings.flash);
  setVal("setting-prefix-spx", settings.spx);
  setVal("setting-prefix-dhl", settings.dhl);
  setVal("setting-prefix-kex", settings.kex);
  setVal("setting-prefix-other", settings.other);

  const tabCarriers = document.getElementById("settings-tab-carriers");
  if (hasRole(["Super Admin", "Shipper"])) {
    if (tabCarriers) tabCarriers.style.display = "flex";
  } else {
    if (tabCarriers) tabCarriers.style.display = "none";
  }

  const tabPackers = document.getElementById("settings-tab-packers");
  if (hasRole(["Super Admin", "Packer & Picker"])) {
    if (tabPackers) tabPackers.style.display = "flex";
  } else {
    if (tabPackers) tabPackers.style.display = "none";
  }

  const tabCleanup = document.getElementById("settings-tab-cleanup");
  const isAllowedCleanup = hasRole(["Super Admin", "Order Manager"]);
  if (tabCleanup) {
    tabCleanup.style.display = isAllowedCleanup ? "flex" : "none";
  }

  const cleanupCountEl = document.getElementById("cleanup-settings-count");
  if (cleanupCountEl) {
    cleanupCountEl.textContent = callbacks.getCleanupNotes().length;
  }

  loadNotificationSettings();
  switchSettingsTab("roles");

  settingsPanel.style.display = "flex";

  document.querySelectorAll(".nav-tab").forEach(tab => {
    if (tab.getAttribute("data-target") === "settings-panel") {
      tab.classList.add("active");
    } else {
      tab.classList.remove("active");
    }
  });

  const isMobile = window.innerWidth <= 1024;
  if (isMobile) {
    if (!history.state || history.state.pane !== "show-settings") {
      history.pushState({ pane: "show-settings" }, "");
    }
  }

  if (window.lucide) window.lucide.createIcons();
}

export function closeSettings(shouldGoBack = true) {
  const settingsPanel = document.getElementById("settings-panel");
  if (settingsPanel) {
    settingsPanel.style.display = "none";
  }

  if (returnToPackerSelectAfterSettings) {
    returnToPackerSelectAfterSettings = false;
    if (typeof callbacks.onReturnToPackerSelect === "function") {
      callbacks.onReturnToPackerSelect();
    }
    return;
  }

  const isMobile = window.innerWidth <= 1024;
  if (isMobile) {
    if (shouldGoBack && history.state && history.state.pane === "show-settings") {
      window.history.back();
      return;
    }
  }

  const appContainer = document.getElementById("app-container");
  const curPane = appContainer && appContainer.classList.contains("show-sidebar")
    ? "show-sidebar"
    : (appContainer && appContainer.classList.contains("show-orders") ? "show-orders" : "show-editor");

  document.querySelectorAll(".nav-tab").forEach(tab => {
    tab.classList.remove("active");
    if (tab.getAttribute("data-target") === curPane.replace("show-", "") + "-panel") {
      tab.classList.add("active");
    }
  });
}

export function saveSettingsData() {
  try {
    const parseInput = (val) => {
      return val ? val.split(",")
        .map(item => item.trim())
        .filter(item => item !== "") : [];
    };

    if (hasRole(["Super Admin", "Shipper"])) {
      const emsEl = document.getElementById("setting-prefix-ems");
      const flashEl = document.getElementById("setting-prefix-flash");
      const spxEl = document.getElementById("setting-prefix-spx");
      const dhlEl = document.getElementById("setting-prefix-dhl");
      const kexEl = document.getElementById("setting-prefix-kex");
      const otherEl = document.getElementById("setting-prefix-other");

      const settings = {
        ems: parseInput(emsEl ? emsEl.value : ""),
        flash: parseInput(flashEl ? flashEl.value : ""),
        spx: parseInput(spxEl ? spxEl.value : ""),
        dhl: parseInput(dhlEl ? dhlEl.value : ""),
        kex: parseInput(kexEl ? kexEl.value : ""),
        other: parseInput(otherEl ? otherEl.value : "")
      };

      localStorage.setItem("carrierSettings", JSON.stringify(settings));
    }

    showToast("บันทึกการตั้งค่ากฎตรวจขนส่งเรียบร้อยแล้ว!");

    const enabledInput = document.getElementById("setting-noti-enabled");
    const enabled = enabledInput ? enabledInput.checked : true;
    localStorage.setItem("notiEnabled", enabled ? "true" : "false");

    const startHour = document.getElementById("setting-noti-start-hour");
    const startMin = document.getElementById("setting-noti-start-minute");
    const startTime = (startHour && startMin) ? `${startHour.value}:${startMin.value}` : "08:00";
    localStorage.setItem("notiStartTime", startTime);

    const endHour = document.getElementById("setting-noti-end-hour");
    const endMin = document.getElementById("setting-noti-end-minute");
    const endTime = (endHour && endMin) ? `${endHour.value}:${endMin.value}` : "18:00";
    localStorage.setItem("notiEndTime", endTime);

    const soundTypeSelect = document.getElementById("setting-noti-sound-type");
    const soundType = soundTypeSelect ? soundTypeSelect.value : "default";
    localStorage.setItem("notiSoundType", soundType);

    const activeDays = [];
    const activeDayButtons = document.querySelectorAll("#noti-days-container .btn-day-toggle.active");
    activeDayButtons.forEach(btn => {
      activeDays.push(parseInt(btn.getAttribute("data-day"), 10));
    });
    const days = activeDays.length > 0 ? activeDays : [1, 2, 3, 4, 5, 6, 0];
    localStorage.setItem("notiDays", JSON.stringify(days));

    const notiSettings = { enabled, startTime, endTime, days, soundType };

    if ("caches" in window) {
      caches.open("noti-settings-cache").then(cache => {
        cache.put("/settings", new Response(JSON.stringify(notiSettings)));
      });
    }

    const currentUserStaff = callbacks.getCurrentUserStaffData();
    if (currentUserStaff && currentUserStaff.email) {
      const staffRef = doc(db, "staff", currentUserStaff.email);
      updateDoc(staffRef, { notificationSettings: notiSettings })
        .then(() => console.log("FCM: Notification settings synced to Firestore successfully."))
        .catch(err => console.error("FCM: Failed to sync notification settings to Firestore:", err));
    }

    closeSettings();

    if (typeof callbacks.onRenderOrderDashboard === "function") {
      callbacks.onRenderOrderDashboard();
    }
  } catch (error) {
    console.error("Error in saveSettingsData:", error);
    showToast("เกิดข้อผิดพลาดในการบันทึกการตั้งค่า", "danger");
  }
}

export function updateCheckboxStates() {
  const checkboxes = document.querySelectorAll(".staff-role-checkbox");
  const checked = Array.from(checkboxes).filter(cb => cb.checked);

  if (checked.length >= 2) {
    checkboxes.forEach(cb => {
      if (!cb.checked) {
        cb.disabled = true;
      }
    });
  } else {
    checkboxes.forEach(cb => {
      cb.disabled = false;
    });
  }
}

export async function loadStaffList() {
  const tbody = document.getElementById("staff-list-tbody");
  if (!tbody) return;

  tbody.innerHTML = `<tr><td colspan="3" style="text-align: center; padding: 12px; color: var(--color-text-muted);">กำลังโหลดข้อมูลพนักงาน...</td></tr>`;

  try {
    const q = query(collection(db, "staff"));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      tbody.innerHTML = `<tr><td colspan="3" style="text-align: center; padding: 12px; color: var(--color-text-muted);">ยังไม่มีพนักงานในระบบ</td></tr>`;
      return;
    }

    tbody.innerHTML = "";
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const email = docSnap.id;
      const displayName = data.displayName || email;
      const roles = data.roles || [];

      const tr = document.createElement("tr");
      tr.style.borderBottom = "1px solid var(--border-color)";

      tr.innerHTML = `
        <td style="padding: 10px; font-weight: 500;">
          <div>${escapeHTML(displayName)}</div>
          <div style="font-size: 0.75rem; color: var(--color-text-muted);">${escapeHTML(email)}</div>
        </td>
        <td style="padding: 10px;">${escapeHTML(roles.join(", "))}</td>
        <td style="padding: 10px; text-align: center; white-space: nowrap;">
          <button type="button" class="btn btn-secondary btn-edit-staff" data-email="${escapeHTML(email)}" data-name="${escapeHTML(displayName)}" data-roles='${escapeHTML(JSON.stringify(roles))}' style="padding: 4px 8px; font-size: 0.75rem; margin-right: 4px;">
            ✏️ แก้ไข
          </button>
          <button type="button" class="btn btn-danger btn-delete-staff" data-email="${escapeHTML(email)}" style="padding: 4px 8px; font-size: 0.75rem; background-color: var(--color-danger); border: none; color: white;">
            🗑️ ลบ
          </button>
        </td>
      `;
      tbody.appendChild(tr);
    });

    tbody.querySelectorAll(".btn-edit-staff").forEach(btn => {
      btn.addEventListener("click", () => {
        const email = btn.getAttribute("data-email");
        const name = btn.getAttribute("data-name");
        const roles = JSON.parse(btn.getAttribute("data-roles"));

        const emailInput = document.getElementById("staff-form-email");
        const nameInput = document.getElementById("staff-form-name");

        if (emailInput) {
          emailInput.value = email;
          emailInput.disabled = true;
        }
        if (nameInput) nameInput.value = name;

        document.querySelectorAll(".staff-role-checkbox").forEach(cb => {
          cb.checked = roles.includes(cb.value);
        });

        updateCheckboxStates();
      });
    });

    tbody.querySelectorAll(".btn-delete-staff").forEach(btn => {
      btn.addEventListener("click", () => {
        const email = btn.getAttribute("data-email");
        const currentUserStaff = callbacks.getCurrentUserStaffData();
        if (currentUserStaff && email === currentUserStaff.email) {
          showToast("ไม่สามารถลบตัวเองได้ครับ", "warning");
          return;
        }
        showConfirm("ยืนยันการลบทีมงาน", `คุณแน่ใจหรือไม่ที่จะลบพนักงานอีเมล ${email} ออกจากระบบ?`, async () => {
          try {
            await deleteDoc(doc(db, "staff", email));
            showToast("ลบข้อมูลพนักงานเรียบร้อยแล้ว");
            loadStaffList();
          } catch (err) {
            console.error("Error deleting staff:", err);
            showToast("ไม่สามารถลบพนักงานได้: " + err.message, "danger");
          }
        });
      });
    });
  } catch (err) {
    console.error("Error loading staff list:", err);
    tbody.innerHTML = `<tr><td colspan="3" style="text-align: center; padding: 12px; color: var(--color-danger);">ไม่สามารถโหลดข้อมูลพนักงานได้</td></tr>`;
  }
}

export async function saveStaffData() {
  const emailInput = document.getElementById("staff-form-email");
  const nameInput = document.getElementById("staff-form-name");

  if (!emailInput || !nameInput) return;

  const email = emailInput.value.trim().toLowerCase();
  const name = nameInput.value.trim();

  if (!email || !name) {
    showToast("กรุณากรอกอีเมลและชื่อพนักงานให้ครบถ้วน", "warning");
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    showToast("กรุณากรอกรูปแบบอีเมลให้ถูกต้อง", "warning");
    return;
  }

  const checkedCheckboxes = document.querySelectorAll(".staff-role-checkbox:checked");
  const roles = Array.from(checkedCheckboxes).map(cb => cb.value);

  if (roles.length === 0) {
    showToast("กรุณาเลือกสิทธิ์อย่างน้อย 1 บทบาท (สูงสุด 2 บทบาท)", "warning");
    return;
  }

  if (roles.length > 2) {
    showToast("สามารถเลือกสิทธิ์ได้สูงสุด 2 บทบาทเท่านั้น", "warning");
    return;
  }

  try {
    showToast("กำลังบันทึกข้อมูล...", "info");

    const docRef = doc(db, "staff", email);
    await setDoc(docRef, {
      displayName: name,
      roles: roles
    }, { merge: true });

    showToast("บันทึกสิทธิ์พนักงานเรียบร้อยแล้ว ✅");

    emailInput.value = "";
    emailInput.disabled = false;
    nameInput.value = "";
    document.querySelectorAll(".staff-role-checkbox").forEach(cb => {
      cb.checked = false;
      cb.disabled = false;
    });

    loadStaffList();
  } catch (err) {
    console.error("Error saving staff:", err);
    showToast("เกิดข้อผิดพลาดในการบันทึกสิทธิ์: " + err.message, "danger");
  }
}

export function setupSettingsModal(options = {}) {
  Object.assign(callbacks, options);

  const btnOpenSettings = document.getElementById("btn-open-settings");
  if (btnOpenSettings) btnOpenSettings.addEventListener("click", showSettings);

  const btnCloseSettings = document.getElementById("btn-close-settings");
  if (btnCloseSettings) btnCloseSettings.addEventListener("click", () => closeSettings());

  const btnCancelSettings = document.getElementById("btn-cancel-settings");
  if (btnCancelSettings) btnCancelSettings.addEventListener("click", () => closeSettings());

  const btnSaveSettings = document.getElementById("btn-save-settings");
  if (btnSaveSettings) btnSaveSettings.addEventListener("click", saveSettingsData);

  const tabCarriers = document.getElementById("settings-tab-carriers");
  const tabRoles = document.getElementById("settings-tab-roles");
  const tabCleanup = document.getElementById("settings-tab-cleanup");
  const tabPackers = document.getElementById("settings-tab-packers");
  const tabNotifications = document.getElementById("settings-tab-notifications");

  if (tabCarriers) tabCarriers.addEventListener("click", () => switchSettingsTab("carriers"));
  if (tabRoles) tabRoles.addEventListener("click", () => switchSettingsTab("roles"));
  if (tabCleanup) tabCleanup.addEventListener("click", () => switchSettingsTab("cleanup"));
  if (tabPackers) tabPackers.addEventListener("click", () => switchSettingsTab("packers"));
  if (tabNotifications) tabNotifications.addEventListener("click", () => switchSettingsTab("notifications"));

  const dayButtons = document.querySelectorAll("#noti-days-container .btn-day-toggle");
  dayButtons.forEach(btn => {
    btn.addEventListener("click", () => {
      btn.classList.toggle("active");
    });
  });

  const soundTypeSelect = document.getElementById("setting-noti-sound-type");
  if (soundTypeSelect) {
    soundTypeSelect.addEventListener("change", (e) => {
      updateCustomSoundUI(e.target.value);
    });
  }

  const customFileInput = document.getElementById("setting-noti-custom-file");
  if (customFileInput) {
    customFileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 3 * 1024 * 1024) {
        showToast("ไฟล์เสียงมีขนาดใหญ่เกิน 3MB กรุณาเลือกไฟล์ที่เล็กลง", "danger");
        customFileInput.value = "";
        return;
      }

      const currentSoundType = soundTypeSelect ? soundTypeSelect.value : "custom1";
      if (!currentSoundType || !currentSoundType.startsWith("custom")) {
        showToast("กรุณาเลือกสล็อตเสียงพิเศษก่อนอัปโหลด", "danger");
        return;
      }

      showToast("กำลังอ่านไฟล์เสียงและอัปโหลด...", "info");

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Url = event.target.result;
        localStorage.setItem(`globalCustomSound_${currentSoundType}`, base64Url);
        localStorage.setItem(`globalCustomFilename_${currentSoundType}`, file.name);

        updateCustomSoundUI(currentSoundType);

        const storageRef = ref(storage, `settings/sound/global_${currentSoundType}`);
        uploadBytes(storageRef, file).then((snapshot) => {
          getDownloadURL(snapshot.ref).then((downloadURL) => {
            const updateObj = {
              [`${currentSoundType}_url`]: downloadURL,
              [`${currentSoundType}_name`]: file.name,
              updatedAt: serverTimestamp()
            };
            setDoc(doc(db, "settings", "sound"), updateObj, { merge: true }).then(() => {
              showToast("อัปโหลดเสียงขึ้นระบบคลาวด์และซิงค์ข้อมูลเสร็จสิ้น! 🌐🎵");
            }).catch(err => {
              console.error("Firestore save sound URL error:", err);
              showToast("บันทึกข้อมูลตำแหน่งเสียงบนคลาวด์ล้มเหลว (เล่นได้เฉพาะเครื่องนี้)", "warning");
            });
          });
        }).catch(err => {
          console.error("Firebase Storage upload error:", err);
          showToast("อัปโหลดไฟล์เสียงขึ้นคลาวด์ล้มเหลว (เล่นได้เฉพาะเครื่องนี้)", "warning");
        });
      };
      reader.readAsDataURL(file);
    });
  }

  const btnClearCustom = document.getElementById("btn-noti-clear-custom");
  if (btnClearCustom) {
    btnClearCustom.addEventListener("click", () => {
      if (customFileInput) customFileInput.value = "";

      const currentSoundType = soundTypeSelect ? soundTypeSelect.value : "custom1";
      if (!currentSoundType || !currentSoundType.startsWith("custom")) {
        showToast("กรุณาเลือกสล็อตเสียงพิเศษก่อนลบ", "danger");
        return;
      }

      showToast("กำลังลบไฟล์เสียงจากระบบคลาวด์...", "info");

      const updateObj = {
        [`${currentSoundType}_url`]: "",
        [`${currentSoundType}_name`]: "",
        updatedAt: serverTimestamp()
      };
      setDoc(doc(db, "settings", "sound"), updateObj, { merge: true }).then(() => {
        const storageRef = ref(storage, `settings/sound/global_${currentSoundType}`);
        deleteObject(storageRef).then(() => {
          showToast("ลบไฟล์เสียงเรียกเข้าส่วนกลางจากระบบคลาวด์เสร็จสิ้น! 🗑️");
        }).catch(err => {
          console.warn("Storage file delete failed or already deleted:", err);
          showToast("ลบไฟล์เสียงเรียกเข้าส่วนกลางเสร็จสิ้น! 🗑️");
        });
      }).catch(err => {
        console.error("Firestore clear sound URL error:", err);
        showToast("เกิดข้อผิดพลาดในการลบข้อมูลเสียง", "danger");
      });
    });
  }

  const btnTestSound = document.getElementById("btn-test-noti-sound");
  if (btnTestSound) {
    btnTestSound.addEventListener("click", () => {
      const selectedType = soundTypeSelect ? soundTypeSelect.value : "default";
      triggerNotificationSound(selectedType);
    });
  }

  const btnTestSystem = document.getElementById("btn-test-noti-system");
  if (btnTestSystem) {
    btnTestSystem.addEventListener("click", () => {
      if (!("Notification" in window)) {
        showToast("เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือนบนหน้าจอ", "danger");
        return;
      }

      const selectedType = soundTypeSelect ? soundTypeSelect.value : "default";

      const triggerTestNotification = async () => {
        triggerNotificationSound(selectedType);

        sendLocalWebNotification({
          id: "test-system",
          customerName: "คุณสมชาย รวยล้นฟ้า (ทดสอบระบบ)",
          items: "สั่งบอร์ดเกมมาถมบ้าน 10ตัน"
        });

        showToast("เริ่มต้นระบบทดสอบ Cloud Push... กรุณาปิดแอปหรือพักหน้าจอมือถือใน 5 วินาที", "info");

        setTimeout(async () => {
          try {
            const noteId = typeof callbacks.getActiveNoteId === "function" ? callbacks.getActiveNoteId() : "temp-note";
            const testOrderPayload = {
              noteId: noteId,
              customerName: "คุณสมชาย รวยล้นฟ้า (ทดสอบระบบ)",
              thaiName: "ทดสอบ แจ้งเตือน",
              phone: "0812345678",
              addressBody: "กรุงเทพมหานคร",
              subDistrict: "ทดสอบ",
              district: "ทดสอบ",
              province: "กรุงเทพมหานคร",
              postalCode: "10110",
              items: "สั่งบอร์ดเกมมาถมบ้าน 10ตัน",
              isPaid: true,
              paymentStatus: "paid",
              deliveryType: "express",
              readyToShip: true,
              isPacked: false,
              pickupDone: false,
              trackingNumber: "",
              carrier: "",
              maxShipments: 1,
              currentShipment: 1,
              trackingHistory: [],
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, "orders"), testOrderPayload);
            const docId = docRef.id;
            console.log("FCM Test Order written with ID:", docId);

            setTimeout(async () => {
              try {
                await deleteDoc(doc(db, "orders", docId));
                console.log("FCM Test Order auto-deleted:", docId);
              } catch (err) {
                console.error("FCM Test Order delete error:", err);
              }
            }, 15000);

          } catch (err) {
            console.error("FCM Test Order write error:", err);
          }
        }, 5000);
      };

      if (Notification.permission === "granted") {
        triggerTestNotification();
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            triggerTestNotification();
          } else {
            showToast("สิทธิ์การแจ้งเตือนถูกปฏิเสธ ไม่สามารถแสดงผลได้", "warning");
          }
        });
      } else {
        showToast("กรุณาเปิดสิทธิ์การแจ้งเตือนในระบบตั้งค่าของเบราว์เซอร์สำหรับเว็บไซต์นี้", "danger");
      }
    });
  }

  const btnGotoCleanup = document.getElementById("btn-goto-cleanup");
  if (btnGotoCleanup) {
    btnGotoCleanup.addEventListener("click", () => {
      closeSettings();
      if (typeof callbacks.onSelectNote === "function") {
        callbacks.onSelectNote("cleanup-queue");
      }
    });
  }

  const staffForm = document.getElementById("staff-form");
  if (staffForm) {
    staffForm.addEventListener("submit", (e) => {
      e.preventDefault();
      saveStaffData();
    });
  }

  const btnSaveStaff = document.getElementById("btn-save-staff");
  if (btnSaveStaff) {
    btnSaveStaff.addEventListener("click", saveStaffData);
  }

  document.querySelectorAll(".staff-role-checkbox").forEach(cb => {
    cb.addEventListener("change", updateCheckboxStates);
  });
}
